/**
 * <cart-page> — cart page behaviour (sections/main-cart.liquid).
 *  - [data-cart-change][data-line][data-quantity]  line quantity / remove via /cart/change.js, then re-renders the section
 *  - [data-cart-note] / [data-cart-gift]            saved to the cart (note + "Gift order" attribute) so they reach checkout
 *  - [data-code-*]                                  discount code is carried to checkout as /checkout?discount=CODE
 *  - [data-checkout]                                saves note, then goes to checkout (with the code when present)
 * Re-renders when any other part of the site fires `cart:updated` (for example the upsell add buttons).
 */
class CartPage extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    const { signal } = this.#abort;
    this.sectionId = this.dataset.sectionId;
    this.checkoutUrl = this.dataset.checkoutUrl || '/checkout';
    this.storageKey = 'rm:cart-discount';

    this.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const change = target.closest('[data-cart-change]');
      if (change instanceof HTMLElement) {
        event.preventDefault();
        this.#change(Number(change.dataset.line), Number(change.dataset.quantity), change.dataset.focusId);
        return;
      }
      if (target.closest('[data-code-apply]')) return this.#applyCode();
      if (target.closest('[data-code-remove]')) return this.#removeCode();
      const checkout = target.closest('[data-checkout]');
      if (checkout) {
        event.preventDefault();
        this.#checkout();
      }
    }, { signal });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && event.target instanceof Element && event.target.matches('[data-code-input]')) {
        event.preventDefault();
        this.#applyCode();
      }
    }, { signal });

    const saveNote = RM.debounce(() => this.#saveNote(), 500);
    this.addEventListener('input', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-cart-note]')) saveNote();
    }, { signal });
    this.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.matches('[data-cart-gift]')) saveNote();
    }, { signal });

    document.addEventListener('cart:updated', (event) => {
      const detail = event.detail || {};
      if (detail.source === 'cart-page') return;
      this.#refresh();
    }, { signal });

    this.#restoreCode();
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  get inner() {
    return this.querySelector('[data-cart-page-inner]');
  }

  /* ---------- cart mutations ---------- */
  async #change(line, quantity, focusId) {
    if (this.#busy || !line) return;
    this.#busy = true;
    this.inner?.setAttribute('aria-busy', 'true');
    try {
      const cart = await RM.fetchJSON(RM.config.routes.cartChange, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line, quantity, sections: this.sectionId, sections_url: window.location.pathname }),
      });
      this.#render(cart.sections && cart.sections[this.sectionId], focusId);
      document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart, sections: cart.sections, source: 'cart-page' } }));
    } catch (error) {
      this.#showError(error.message || RM.config.strings.cartError);
    } finally {
      this.#busy = false;
      this.inner?.removeAttribute('aria-busy');
    }
  }

  async #saveNote() {
    const gift = this.querySelector('[data-cart-gift]');
    const note = this.querySelector('[data-cart-note]');
    if (!note) return;
    const payload = { note: note.value };
    if (gift) payload.attributes = { 'Gift order': gift.checked ? 'Yes' : '' };
    try {
      await RM.fetchJSON(RM.config.routes.cartUpdate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
    } catch (error) {
      this.#showError(error.message);
    }
  }

  async #refresh() {
    try {
      this.#render(await RM.fetchSection(this.sectionId));
    } catch (error) {
      this.#showError(RM.config.strings.cartError);
    }
  }

  #render(html, focusId) {
    if (!html) return this.#refresh();
    const next = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-cart-page-inner]');
    const current = this.inner;
    if (!next || !current) return undefined;
    const activeFocusId = focusId || (document.activeElement instanceof HTMLElement ? document.activeElement.dataset.focusId : '');
    current.innerHTML = next.innerHTML;
    this.#restoreCode();
    if (activeFocusId) {
      const target = this.querySelector(`[data-focus-id="${CSS.escape(activeFocusId)}"]`) || this.querySelector('[data-cart-change]');
      if (target instanceof HTMLElement) target.focus();
    }
    return undefined;
  }

  #showError(message) {
    const box = this.querySelector('[data-cart-error]');
    if (!box) return;
    box.textContent = message || RM.config.strings.cartError;
    box.hidden = false;
    setTimeout(() => { box.hidden = true; }, 6000);
  }

  /* ---------- discount code ---------- */
  #storedCode() {
    try {
      return window.sessionStorage.getItem(this.storageKey) || '';
    } catch (error) {
      return '';
    }
  }

  #storeCode(code) {
    try {
      if (code) window.sessionStorage.setItem(this.storageKey, code);
      else window.sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      /* storage unavailable: the code is kept for this view only */
    }
  }

  #applyCode() {
    const input = this.querySelector('[data-code-input]');
    const error = this.querySelector('[data-code-error]');
    const code = (input?.value || '').trim().toUpperCase();
    if (!code) {
      if (error) error.hidden = false;
      input?.focus();
      return;
    }
    if (error) error.hidden = true;
    this.#storeCode(code);
    this.#restoreCode();
  }

  #removeCode() {
    this.#storeCode('');
    const input = this.querySelector('[data-code-input]');
    if (input) input.value = '';
    this.#restoreCode();
    input?.focus();
  }

  #restoreCode() {
    const code = this.#storedCode();
    const applied = this.querySelector('[data-code-applied]');
    const label = this.querySelector('[data-code-label]');
    const row = this.querySelector('.cart-code__row');
    if (!applied) return;
    const details = applied.closest('details');
    if (details && code) details.open = true;
    applied.hidden = !code;
    if (row) row.hidden = Boolean(code);
    if (label) label.textContent = code;
  }

  async #checkout() {
    await this.#saveNote();
    const code = this.#storedCode();
    const url = new URL(this.checkoutUrl, window.location.origin);
    if (code) url.searchParams.set('discount', code);
    window.location.href = url.toString();
  }
}

if (!customElements.get('cart-page')) customElements.define('cart-page', CartPage);
